import { useState } from "react";
import Layout from "@/components/layout/Layout";
import PageHero from "@/components/shared/PageHero";
import PartnersSection from "@/components/home/PartnersSection";
import SEOHead from "@/components/seo/SEOHead";
import SchemaMarkup from "@/components/seo/SchemaMarkup";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Mail, Phone, MapPin, Clock, Send, Loader2 } from "lucide-react";
import { toast } from "@/hooks/use-toast";
import { z } from "zod";

const contactInfo = [
  {
    icon: Phone,
    title: "Phone",
    details: ["+880 9649 660000"],
    link: "tel:+8809649660000",
    linkType: "tel" as const,
  },
  {
    icon: Mail,
    title: "Email",
    details: ["hello@webhostbuybd.com"],
    link: "mailto:hello@webhostbuybd.com",
    linkType: "email" as const,
  },
  {
    icon: MapPin,
    title: "Address",
    details: ["10 Nur Vobon (2nd Floor)", "Nogor Vobon Road, Manikganj"],
  },
  {
    icon: Clock,
    title: "Business Hours",
    details: ["24/7 Support Available", "Office: Sun-Thu, 10AM-6PM"],
  },
];

// Validation schema
const contactSchema = z.object({
  name: z.string().trim().min(1, "Name is required").max(100, "Name must be less than 100 characters"),
  email: z.string().trim().email("Please enter a valid email address").max(255, "Email must be less than 255 characters"),
  subject: z.string().trim().min(1, "Subject is required").max(200, "Subject must be less than 200 characters"),
  message: z.string().trim().min(10, "Message must be at least 10 characters").max(2000, "Message must be less than 2000 characters"),
});

const Contact = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    subject: "",
    message: "",
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const validateField = (name: string, value: string) => {
    try {
      const fieldSchema = contactSchema.shape[name as keyof typeof contactSchema.shape];
      fieldSchema.parse(value);
      setErrors((prev) => ({ ...prev, [name]: "" }));
    } catch (error) {
      if (error instanceof z.ZodError) {
        setErrors((prev) => ({ ...prev, [name]: error.errors[0]?.message || "" }));
      }
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    validateField(name, value);
  };

  const MAIL_ENDPOINT = "https://webhostbuybd.com/mail.php";

  // Allowlist of safe server response messages
  const SAFE_MESSAGES: Record<string, string> = {
    success: "Thank you for contacting Web Host Buy BD. We'll get back to you within 24 hours.",
    validation_failed: "Please check your form inputs and try again.",
    send_failed: "Failed to send email. Please try again or contact us directly at hello@webhostbuybd.com",
    default_error: "Something went wrong. Please try again.",
  };

  const getSafeMessage = (serverMessage: string | undefined, fallbackKey: string): string => {
    // Only use server message if it matches a known safe message
    const safeValues = Object.values(SAFE_MESSAGES);
    if (serverMessage && safeValues.some(safe => serverMessage.startsWith(safe.substring(0, 20)))) {
      return serverMessage;
    }
    return SAFE_MESSAGES[fallbackKey] || SAFE_MESSAGES.default_error;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!MAIL_ENDPOINT) {
      toast({
        title: "Configuration Error",
        description: "Contact form is not configured. Please email us directly at hello@webhostbuybd.com",
        variant: "destructive",
      });
      return;
    }

    setIsSubmitting(true);

    try {
      // Validate all fields
      const validatedData = contactSchema.parse(formData);
      
      // Send to PHP backend with honeypot field
      const response = await fetch(MAIL_ENDPOINT, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ ...validatedData, website: "" }),
      });

      const result = await response.json();

      if (result.success) {
        toast({
          title: "Message Sent!",
          description: getSafeMessage(result.message, "success"),
        });
        setFormData({ name: "", email: "", subject: "", message: "" });
        setErrors({});
      } else {
        toast({
          title: "Failed to Send",
          description: getSafeMessage(result.message, "default_error"),
          variant: "destructive",
        });
      }
    } catch (error) {
      if (error instanceof z.ZodError) {
        const newErrors: Record<string, string> = {};
        error.errors.forEach((err) => {
          if (err.path[0]) {
            newErrors[err.path[0] as string] = err.message;
          }
        });
        setErrors(newErrors);
        toast({
          title: "Validation Error",
          description: "Please correct the errors in the form.",
          variant: "destructive",
        });
      } else {
        toast({
          title: "Connection Error",
          description: "Could not connect to server. Please try again later or email us directly.",
          variant: "destructive",
        });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Layout>
      <SEOHead
        title="Contact Web Host Buy BD | 24/7 BDIX Hosting Support &amp; Sales"
        description="Contact Web Host Buy BD for BDIX hosting support, sales &amp; technical inquiries. 24/7 expert support via phone, email &amp; live chat. Best hosting provider in Bangladesh with fastest response time."
        canonical="/contact-us"
        keywords="Web Host Buy BD, best hosting provider in Bangladesh, bdix hosting"
      />
      <SchemaMarkup
        type="contact"
        breadcrumbs={[
          { name: "Home", url: "/" },
          { name: "Contact", url: "/contact-us" },
        ]}
        pageName="Contact Web Host Buy BD"
        pageDescription="Get in touch with Web Host Buy BD for BDIX hosting support and sales in Bangladesh"
      />
      <PageHero
        badge="Contact Us"
        title="Get in Touch"
        subtitle="Have questions about BDIX hosting? Our team is here to help 24/7. Reach out and we'll respond as soon as possible."
      />

      <section className="py-20 bg-background">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-12">
            {/* Contact Form */}
            <div>
              <h2 className="text-2xl font-bold text-foreground mb-6 text-center lg:text-left">
                Send us a Message
              </h2>
              <form onSubmit={handleSubmit} className="space-y-6" noValidate>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-foreground mb-2">
                      Your Name <span className="text-destructive">*</span>
                    </label>
                    <Input
                      id="name"
                      name="name"
                      type="text"
                      placeholder="John Doe"
                      value={formData.name}
                      onChange={handleChange}
                      className={errors.name ? "border-destructive" : ""}
                      required
                      aria-invalid={!!errors.name}
                      aria-describedby={errors.name ? "name-error" : undefined}
                    />
                    {errors.name && (
                      <p id="name-error" className="text-destructive text-sm mt-1">{errors.name}</p>
                    )}
                  </div>
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-foreground mb-2">
                      Email Address <span className="text-destructive">*</span>
                    </label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      placeholder="john@example.com"
                      value={formData.email}
                      onChange={handleChange}
                      className={errors.email ? "border-destructive" : ""}
                      required
                      aria-invalid={!!errors.email}
                      aria-describedby={errors.email ? "email-error" : undefined}
                    />
                    {errors.email && (
                      <p id="email-error" className="text-destructive text-sm mt-1">{errors.email}</p>
                    )}
                  </div>
                </div>
                <div>
                  <label htmlFor="subject" className="block text-sm font-medium text-foreground mb-2">
                    Subject <span className="text-destructive">*</span>
                  </label>
                  <Input
                    id="subject"
                    name="subject"
                    type="text"
                    placeholder="How can we help with your BDIX hosting?"
                    value={formData.subject}
                    onChange={handleChange}
                    className={errors.subject ? "border-destructive" : ""}
                    required
                    aria-invalid={!!errors.subject}
                    aria-describedby={errors.subject ? "subject-error" : undefined}
                  />
                  {errors.subject && (
                    <p id="subject-error" className="text-destructive text-sm mt-1">{errors.subject}</p>
                  )}
                </div>
                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-foreground mb-2">
                    Message <span className="text-destructive">*</span>
                  </label>
                  <Textarea
                    id="message"
                    name="message"
                    placeholder="Tell us more about your inquiry..."
                    rows={6}
                    value={formData.message}
                    onChange={handleChange}
                    className={errors.message ? "border-destructive" : ""}
                    required
                    aria-invalid={!!errors.message}
                    aria-describedby={errors.message ? "message-error" : undefined}
                  />
                  {errors.message && (
                    <p id="message-error" className="text-destructive text-sm mt-1">{errors.message}</p>
                  )}
                </div>
                <Button 
                  variant="gradient" 
                  size="lg" 
                  type="submit" 
                  className="w-full md:w-auto"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" aria-hidden="true" />
                      Sending...
                    </>
                  ) : (
                    <>
                      <Send className="w-4 h-4 mr-2" aria-hidden="true" />
                      Send Message
                    </>
                  )}
                </Button>
              </form>
            </div>

            {/* Contact Info */}
            <aside className="space-y-8">
              <h2 className="text-2xl font-bold text-foreground mb-6 text-center lg:text-left">
                Contact Information
              </h2>
              
              <div className="grid sm:grid-cols-2 gap-6">
                {contactInfo.map((item) => (
                  <article
                    key={item.title}
                    className="p-6 rounded-xl bg-card border border-border text-center sm:text-left"
                  >
                    <div className="w-12 h-12 gradient-primary rounded-xl flex items-center justify-center mb-4 mx-auto sm:mx-0" aria-hidden="true">
                      <item.icon className="w-6 h-6 text-primary-foreground" />
                    </div>
                    <h3 className="font-semibold text-foreground mb-2">
                      {item.title}
                    </h3>
                    {item.details.map((detail, index) => (
                      item.link ? (
                        <a 
                          key={index} 
                          href={item.link}
                          className="block text-muted-foreground text-sm hover:text-primary transition-colors"
                        >
                          {detail}
                        </a>
                      ) : (
                        <p key={index} className="text-muted-foreground text-sm">
                          {detail}
                        </p>
                      )
                    ))}
                  </article>
                ))}
              </div>

              {/* Map */}
              <div className="rounded-xl overflow-hidden border border-border">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d58467.04!2d90.0047!3d23.8617!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x39fe4b64c91c4381%3A0x9e8c6c1c0f90f1b0!2sManikganj!5e0!3m2!1sen!2sbd!4v1234567890"
                  width="100%"
                  height="250"
                  style={{ border: 0 }}
                  allowFullScreen
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Web Host Buy BD Office Location - Manikganj, Bangladesh"
                />
              </div>
            </aside>
          </div>
        </div>
      </section>

      <PartnersSection />
    </Layout>
  );
};

export default Contact;